import { Accordion, AccordionDetails, AccordionSummary, Box, Link, Skeleton, useTheme } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { fetchBiochallengeInfo, selectFilteredBiochallengeInfoByFacilities, selectFilteredDeviceConfigsByFacilities, setBiochallengeInfo } from '../summaryDashboardSlice';
import { ExpandMoreTwoTone, WarningTwoTone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SubCard from 'src/components/material/Cards/SubCard';

const BioChallengeTable = ({ customerIdParam }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation("translations");
    const { biochallengeInfo, selectedFacilities, selectedCustomer } = useSelector((state) => state.summarydashboard);

    const loading = useSelector((state) => state.summarydashboard.loading.biochallengeInfo);

    const filteredDeviceConfigs = useSelector((state) => selectFilteredDeviceConfigsByFacilities(state.summarydashboard, selectedFacilities.map((facility) => facility.id)));
    const filteredBiochallengeInfo = useSelector((state) => selectFilteredBiochallengeInfoByFacilities(state.summarydashboard, selectedFacilities.map((facility) => facility.id)));

    const theme = useTheme();

    useEffect(() => {
        if (selectedCustomer) {
            dispatch(fetchBiochallengeInfo(selectedCustomer.id));
        }
    }, [selectedCustomer]);

    useEffect(() => {
        if (customerIdParam) {
            dispatch(fetchBiochallengeInfo(customerIdParam));
        }
    }, [customerIdParam]);


    // useEffect(() => {
    //     if (filteredDeviceConfigs.length > 0) {
    //         dispatch(fetchBiochallengeInfo(selectedCustomer.id));
    //     } else {
    //         dispatch(setBiochallengeInfo([]));
    //     }
    // }, [selectedFacilities]);


    const getBioCellFormatting = (params) => {
        if (params.row.type === "calendar") {
            const daysTill = DateTime.fromISO(params.row.next_biochallenge_date).diff(DateTime.now(), "days").days;
            return clsx("due-date-table", {
                ok: daysTill >= 7,
                warn: daysTill < 7 && daysTill > 1,
                past: daysTill <= 1,
            });
        } else if (params.row.type === "usage") {
            const cyclesTill = params.row.cycles_till_biochallenge;
            return clsx("due-date-table", {
                ok: cyclesTill >= 14,
                warn: cyclesTill < 14 && cyclesTill > 5,
                past: cyclesTill <= 1,
            });
        }
    };

    const dueDateAccordian = (params) => {
        if (params.row.type === "usage") {
            const cyclesTill = params.row.cycles_till_biochallenge;
            return (
                <Accordion sx={{
                    ...(cyclesTill >= 14 && { bgcolor: theme.palette.dueDate.ok, color: theme.palette.common.white }),
                    ...(cyclesTill > 5 && cyclesTill < 14 && { bgcolor: theme.palette.dueDate.warning }),
                    ...(cyclesTill <= 1 && { bgcolor: theme.palette.dueDate.due, color: theme.palette.common.white }),
                }}
                    data-search={`${cyclesTill} ${t("Cycles")}`}
                >
                    <AccordionSummary sx={{ flexDirection: "row-reverse" }} expandIcon={<ExpandMoreTwoTone sx={{ color: theme.palette.common.white }} />}>
                        {cyclesTill} {t("Cycles")}
                    </AccordionSummary>
                    <AccordionDetails>
                        {cyclesTill} {t("cycles remaining before a Biological Challenge is due")}
                    </AccordionDetails>
                </Accordion>
            )
        }
        const currentDate = DateTime.now().toUTC();

        const nextBiochallengeDate = DateTime.fromISO(params.row.next_biochallenge_date).toUTC();

        const daysTillDueDate = nextBiochallengeDate.diff(currentDate, "days").days;
        const prettyDueDate = nextBiochallengeDate.toRelative();

        return (
            <Accordion sx={{
                ...(daysTillDueDate >= 7 && { bgcolor: theme.palette.dueDate.ok, color: theme.palette.common.white }),
                ...(daysTillDueDate > 1 &&
                    daysTillDueDate < 7 && { bgcolor: theme.palette.dueDate.warning }),
                ...(daysTillDueDate <= 1 && { bgcolor: theme.palette.dueDate.due, color: theme.palette.common.white }),
            }}
                data-date={prettyDueDate}
                data-search={`${prettyDueDate} - ${nextBiochallengeDate.toISO()}`}
            >
                <AccordionSummary
                    sx={{ flexDirection: "row-reverse" }}
                    expandIcon={<ExpandMoreTwoTone sx={{ color: theme.palette.common.white }} />}>
                    {prettyDueDate.charAt(0).toUpperCase() + prettyDueDate.slice(1)}
                </AccordionSummary>
                <AccordionDetails>{nextBiochallengeDate.toLocal().toLocaleString(DateTime.DATETIME_FULL)}</AccordionDetails>
            </Accordion>
        )
    }

    const getBiochallengeComments = (params) => {
        if (params.row.x_series_value < 0) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", color: theme.palette.error.main }}>
                    <WarningTwoTone sx={{ mr: 1 }} />
                    {t("A Biological Challenge must be run before this device can run a Sterilization cycle")}
                </Box>
            )
        }
    }

    const getBioStatusColor = (params) => {
        const result = params.row.last_biochallenge_result;
        if (result === 1) {
            return theme.palette.success.main;
        } else if (result === 0) {
            return theme.palette.error.main;
        } else {
            return theme.palette.warning.main;
        }
    }


    const columns = [
        {
            headerName: "Device Serial",
            field: "serial_number",
            headerAlign: "center",
            headerClassName: "due-date-table--header",
            align: "center",
            flex: 0.6,
            renderCell: ({ value }) => (
                <Link target="_blank" to={`/find-a-machine?serial=${value}`}>
                    {value}
                </Link>
            ),
        },
        {
            headerName: "Bio-Challenge Due",
            field: "next_biochallenge_date",
            headerAlign: "center",
            headerClassName: "due-date-table--header",
            align: "center",
            flex: 1,
            renderCell: (params) => dueDateAccordian(params),
            cellClassName: (params) => getBioCellFormatting(params),
        },
        {
            headerName: "Last Bio-Challenge Date",
            field: "last_biochallenge_validation_date",
            headerAlign: "center",
            headerClassName: "due-date-table--header",
            align: "center",
            flex: 1,
            renderCell: (params) => (
                <Box>
                    {DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_FULL)}
                    {params.row.last_biochallenge_status_msg && (
                        <Box sx={{ color: getBioStatusColor(params), justifyContent: "center", alignItems: "center", display: "flex", marginTop: 0.5 }}>
                            ({params.row.last_biochallenge_status_msg})
                        </Box>
                    )}
                </Box>
            ),
        },
        {
            headerName: "Run By",
            field: "last_biochallenge_operator",
            headerAlign: "center",
            headerClassName: "due-date-table--header",
            align: "center",
            flex: 0.75,
            renderCell: (params) => params.value,
        },
        {
            headerName: "Comments",
            field: "comments",
            headerAlign: "center",
            headerClassName: "due-date-table--header",
            align: "center",
            flex: 1,
            renderCell: (params) => getBiochallengeComments(params),
        }
    ];

    const Toolbar = (props) => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                }}>
                <Box sx={{ flex: 1, padding: 1 }}>
                    <GridToolbarQuickFilter />
                </Box>
            </GridToolbarContainer>
        );
    };

    return (
        <SubCard
            title="Bio Challenges"
            titleProps={{ color: "primary", fontWeight: "bold" }}
            titleVariant={"h4"}
            sx={{ width: "100%" }}
        >
            <Box
                sx={{
                    width: "100%",
                    "& .due-date-table--header": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    },
                    "& .due-date-table.ok": {
                        backgroundColor: theme.palette.dueDate.ok,
                    },
                    "& .due-date-table.warn": {
                        backgroundColor: theme.palette.dueDate.warning,
                    },
                    "& .due-date-table.past": {
                        backgroundColor: theme.palette.dueDate.due,
                    },
                }}>
                {loading ? (
                    <Skeleton variant="rounded" animation="wave" width="100%" height={300} />
                ) : (
                    <DataGrid
                        disableSelectionOnClick
                        autoHeight
                        // @ts-ignore
                        columns={columns}
                        rows={filteredBiochallengeInfo}
                        density="comfortable"
                        sx={{
                            fontSize: 14,
                        }}
                        getRowHeight={() => {
                            return "auto";
                        }}
                        getRowId={(params) => params.serial_number}
                        components={{ Toolbar: Toolbar }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                    />
                )}
            </Box>
        </SubCard>
    )
}

export default BioChallengeTable
